import React, { useState } from "react";
import { useCreate } from "@pankod/refine-core";
import { Button, Modal, Space, Typography, Alert, notificationProvider } from "@pankod/refine-antd";
import { ICompany } from "interfaces";

const { Paragraph, Text } = Typography;

interface SeedTreasuryAutoApproveModelProps {
    companyId: string;
    companyDetail: ICompany;
    refetchCompanyDetail: (shouldRefetch: boolean) => void;
}

/**
 * Ops action: publish THIS company's treasury (North -> KodoPay) auto-approve workflow
 * (KODO_PAY / TREASURY_PAYMENT): one auto-approved level, no conditions. Treasury payments run on it
 * instead of the company's Single Payment workflow, which is not touched. Re-running republishes it.
 * Without it, every treasury payment for the company fails.
 *
 * PRECONDITION (enforced by the backend, surfaced here as an error): the ops user must be a
 * SUPER_ADMIN or BACKEND_SCRIPT_RUNNER (no need to switch into the company).
 */
export const SeedTreasuryAutoApproveModel: React.FC<SeedTreasuryAutoApproveModelProps> = ({
    companyId,
    companyDetail,
    refetchCompanyDetail,
}) => {
    const [open, setOpen] = useState(false);
    const { mutate, isLoading } = useCreate();

    const handleSeed = () => {
        mutate(
            {
                resource: "pvtSeedTreasuryAutoApproveWorkflowForCompany",
                dataProviderName: "grapghQl",
                values: { input: { companyId } },
                successNotification: false,
                errorNotification: false,
            },
            {
                onSuccess: () => {
                    notificationProvider.open({
                        message: "Treasury auto-approve workflow seeded",
                        type: "success",
                        description: `Treasury workflow published for ${companyDetail?.registeredName ?? "this company"}.`,
                    });
                    setOpen(false);
                    refetchCompanyDetail(true);
                },
                onError: (err: any) => {
                    notificationProvider.open({
                        message: err?.message || "Failed to seed treasury auto-approve workflow",
                        type: "error",
                        description: "",
                    });
                },
            },
        );
    };

    return (
        <Space>
            <Button type="default" onClick={() => setOpen(true)}>
                Seed Treasury Auto-Approve
            </Button>

            <Modal
                title="Seed Treasury Auto-Approve Workflow"
                visible={open}
                centered
                width={620}
                okText="Seed workflow"
                okButtonProps={{ loading: isLoading }}
                cancelButtonProps={{ disabled: isLoading }}
                onOk={handleSeed}
                onCancel={() => setOpen(false)}
            >
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Alert
                        type="info"
                        showIcon
                        message="This does not change the company's own workflows."
                        description="It publishes a separate Treasury Payment workflow for this company with one auto-approved level. Treasury payments use it instead of the Single Payment workflow, so direct KodoPay approvals are unaffected. Re-running republishes the same workflow."
                    />
                    <Paragraph style={{ marginBottom: 0 }}>
                        <Space direction="vertical" size={2}>
                            <Text>Company: <Text strong>{companyDetail?.registeredName ?? companyId}</Text></Text>
                            <Text type="secondary">
                                Treasury payments for this company fail until this has been run.
                            </Text>
                        </Space>
                    </Paragraph>
                </Space>
            </Modal>
        </Space>
    );
};
